import { useNavigate, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '@/shared/api/client';
import { authenticateResponseSchema } from '@/shared/api/schemas.api';
import { transformAuthResponse } from '@/shared/api/transforms';
import { QUERY_KEYS } from '@/shared/lib/constants';
import { logger } from '@/shared/lib/logger';
import { getVendorToken, setVendorToken, clearVendorToken } from '@/shared/lib/vendor-token';
import { getSocketManager } from '@/shared/lib/socket-manager';
import type { AuthSessionData } from '@/shared/api/types';

// Re-export for existing consumers (socket hook)
export { getVendorToken } from '@/shared/lib/vendor-token';

// Read token from URL on module load so it's available before any hook runs
const urlToken = new URL(window.location.href).searchParams.get('token');
if (urlToken) {
  setVendorToken(urlToken);
  // Strip token from URL — cookie persists auth across refresh
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete('token');
  history.replaceState(null, '', cleanUrl.toString());
}

const TERMINAL_PATHS = ['/expired', '/not-found'];

export function useAuthenticate() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isTerminalPage = TERMINAL_PATHS.includes(pathname);

  const query = useQuery<AuthSessionData>({
    queryKey: QUERY_KEYS.authSession,
    queryFn: async () => {
      // Build headers: include token only if available (first visit)
      // On refresh, cookie-based auth is used (credentials: 'include' in api client)
      const currentToken = getVendorToken();
      const headers: Record<string, string> = {};
      if (currentToken) {
        headers.vendor_token = currentToken;
      }
      const raw = await api.post<unknown>('/v0/vendor/authenticate', undefined, {
        headers,
      });
      const result = authenticateResponseSchema.safeParse(raw);
      if (!result.success) {
        logger.error('Auth response validation failed', {
          issues: JSON.stringify(result.error.issues),
        });
        throw new Error(
          `Auth validation failed: ${result.error.issues.map((i) => i.message).join(', ')}`,
        );
      }
      return transformAuthResponse(result.data.data);
    },
    // Skip auth on terminal pages (/expired, /not-found) to avoid wasted 401 requests
    enabled: !isTerminalPage,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, error) => {
      if (error instanceof ApiClientError && error.status === 401) {
        getSocketManager().disconnect();
        clearVendorToken();
        void navigate('/expired', { replace: true });
        return false;
      }
      return failureCount < 2;
    },
  });

  return {
    data: query.data,
    isPending: query.isPending,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
